/**
 * Copyright © 2026 Adnuntius AS.
 */
import {AdnListHelperService} from '../../common/controller/list-helper.service';
import {ChangeDetectorRef, Component, Inject, Input, NgZone} from '@angular/core';
import * as _ from 'lodash';
import {TranslateService} from '@ngx-translate/core';

import template from './targeting-template.component.html';
import {Uuid} from '../../../components/util/uuid';
import {TargetingHelper} from '../targeting-helper';
import {TeamResource} from '../../../components/api/resources/resource/team.resource';
import {TargetingTemplateResource} from '../../../components/api/resources/resource/targeting-template.resource';
import {LocalUserPermissions} from '../../../components/session/local-user-permissions';
import {deferMarkForCheck} from '../../common/util/defer-mark-for-check';

export interface TargetingTemplateHook {
  getTemplateData: () => {
    vm: any;
    model: any;
    constant: any;
    summary?: string;
  };
  copyTemplate: (template: any) => void;
}

@Component({
  standalone: false,
  selector: 'adn-targeting-template',
  template
})
export class TargetingTemplateComponent {
  @Input() hook: TargetingTemplateHook;
  @Input() mode: string;
  @Input() isLockedDown: boolean;
  @Input() targetType: any;
  @Input() overridableTargeting: any;
  @Input() mandatory: boolean;
  @Input() underModel: any;

  enabled = false;
  saved = false;
  view: 'COPY' | 'SAVE' | null = null;
  saveCount = 0;
  refreshCopy = false;

  /** Buffered for template — avoids NG0100 when list-helper fills items mid-refresh. */
  listReady = false;
  hasItems = false;
  displayItems: any[] = [];
  listPageSize = 25;
  listTotalCount = 0;
  listCurrentPage = 1;

  copyTemplateList: any;
  saveModel: any;

  allTeams: any[];
  yourTeams: any[] = [];
  teams: any[];

  constructor(
    private readonly adnListHelper: AdnListHelperService,
    @Inject(TeamResource) private readonly Team: any,
    @Inject(LocalUserPermissions) private readonly LocalUserPermissions: any,
    @Inject(TargetingTemplateResource) private readonly TargetingTemplate: any,
    private readonly translate: TranslateService,
    private readonly ngZone: NgZone,
    private readonly cdr: ChangeDetectorRef
  ) {
  }

  get overrideChecked(): boolean {
    return !!(this.overridableTargeting && this.targetType && this.overridableTargeting[this.targetType.apiType]);
  }

  set overrideChecked(value: boolean) {
    if (this.overridableTargeting && this.targetType) {
      this.overridableTargeting[this.targetType.apiType] = value;
    }
  }

  toggleCopy(): void {
    this.enabled = this.view === 'SAVE' ? true : !this.enabled;
    this.saved = false;

    if (!this.enabled || !this.hook) {
      return;
    }

    this.view = 'COPY';
    const tplData = this.hook.getTemplateData();

    if (!this.copyTemplateList) {
      this.listReady = false;
      this.hasItems = false;
      this.copyTemplateList = {items: []};
      this.adnListHelper.setUpBasicList(
        this.copyTemplateList,
        this.TargetingTemplate,
        'targetingTargetingTemplates',
        {
          filterBy: 'type',
          filterByLike: tplData.constant.templateType,
          afterFill: () => {
            deferMarkForCheck(this.cdr, () => {
              // Snapshot — do not bind copyTemplateList.items (list-helper
              // replaces that array on settle and can race checkNoChanges).
              this.displayItems = (this.copyTemplateList.items || []).slice();
              this.hasItems = this.displayItems.length > 0;
              this.listPageSize = this.copyTemplateList.pageSize || 25;
              this.listTotalCount = this.copyTemplateList.totalCount || 0;
              this.listCurrentPage = this.copyTemplateList.currentPage || 1;
              this.listReady = true;
            });
          }
        }
      );
    } else if (this.refreshCopy) {
      this.reloadList();
      this.refreshCopy = false;
    }
  }

  toggleSaved(): void {
    this.enabled = this.view !== 'SAVE' ? true : !this.enabled;
    this.saved = false;

    if (!this.enabled || !this.hook) {
      return;
    }

    this.view = 'SAVE';
    const tplData: any = this.hook.getTemplateData();
    tplData.count = TargetingHelper.getSizeOfTargetingTypes(tplData.model, tplData.constant.targets);
    this.saveCount = tplData.count;

    this.saveModel = {data: {}};
    const typeId = tplData.constant.id;
    const labelKey = tplData.count === 1
      ? 'targeting.' + typeId + '.' + typeId
      : 'targeting.' + typeId + '.plural';
    let typeLabel = this.translate.instant(labelKey);
    if (typeof typeLabel !== 'string') {
      typeLabel = this.translate.instant('targeting.' + typeId + '.plural');
    }
    if (typeof typeLabel !== 'string') {
      typeLabel = typeId;
    }
    this.saveModel.name = tplData.count + ' ' + typeLabel;
    this.saveModel.description = tplData.summary || this.saveModel.name;
    this.saveModel.data.model = tplData.model;
    this.saveModel.data.vm = tplData.vm;
    this.saveModel.type = tplData.constant.templateType;
    this.saveModel.teams = this.saveModel.teams || [];

    Promise.resolve(this.teamQuery(this.saveModel)).then(() => {
      this.ngZone.run(() => {
        this.saveModel.teamType = 'YOUR';
      });
    });
  }

  selectTeams(template?: any): void {
    const target = template || this.saveModel;
    target.teams = _.map(this.allTeams, 'id');
  }

  deselectTeams(template?: any): void {
    const target = template || this.saveModel;
    target.teams = [];
  }

  editInvoked(template: any): void {
    template.editable = true;
    template.teams = _.map(template.teams, (team) => team.id || team);
    this.teamQuery(template);
  }

  editCancelled(template: any): void {
    template.editable = false;
  }

  copyInvoked(template: any): void {
    this.hook.copyTemplate(template);
    _.forEach(this.displayItems, (item) => {
      item.copy = false;
    });
    template.copy = true;
  }

  deleteInvoked(template: any): void {
    template.$archive();
    setTimeout(() => {
      this.ngZone.run(() => {
        this.reloadList();
      });
    }, 150);
  }

  editTemplate(template: any): void {
    const templateForSave = _.cloneDeep(template);
    const nt = new this.TargetingTemplate(templateForSave);

    if (!nt.teamType || nt.teamType === 'YOUR') {
      nt.teams = this.yourTeams;
    }

    nt.savePartial();
    this.enabled = false;
    this.saved = true;
    this.refreshCopy = true;
  }

  saveTemplate(): void {
    const templateForSave = _.cloneDeep(this.saveModel);
    templateForSave.id = Uuid.generate();
    const nt = new this.TargetingTemplate(templateForSave);

    if (this.saveModel.teamType === 'ALL') {
      nt.teams = [];
    } else if (this.saveModel.teamType === 'YOUR') {
      nt.teams = this.yourTeams;
    }

    nt.savePartial();
    this.enabled = false;
    this.saved = true;
    this.refreshCopy = true;
  }

  onSaveEnter(event: KeyboardEvent): void {
    event.preventDefault();
    this.saveTemplate();
  }

  changePage(page: number): void {
    if (!this.copyTemplateList || page < 1) {
      return;
    }
    this.copyTemplateList.currentPage = page;
    this.copyTemplateList.changePage();
  }

  pageStart(): number {
    return this.listPageSize * (this.listCurrentPage - 1) + 1;
  }

  pageEnd(): number {
    return Math.min(this.listPageSize * this.listCurrentPage, this.listTotalCount);
  }

  private reloadList(): void {
    this.listReady = false;
    this.hasItems = false;
    this.displayItems = [];
    this.listPageSize = 25;
    this.listTotalCount = 0;
    this.listCurrentPage = 1;
    this.copyTemplateList.makeQuery();
  }

  private teamQuery(template: any): Promise<void> | void {
    const doType = () => {
      template.teamType = _.get(this, ['yourTeams', 'length']) === _.get(template, ['teams', 'length'])
        ? 'YOUR'
        : 'CUSTOM';
    };

    if (this.allTeams) {
      doType();
      return;
    }

    return this.Team.query({minimal: true}).$promise.then((page: any) => {
      return new Promise<void>((resolve) => {
        deferMarkForCheck(this.cdr, () => {
          this.allTeams = page.results;
          const isViewAllObjects = this.LocalUserPermissions.hasNetwork('VIEW_ALL_OBJECTS');
          this.yourTeams = _.filter(this.allTeams, (g) => {
            return this.LocalUserPermissions.isPartOf(g.network.tagId || g.network.id, g.id);
          });
          this.teams = isViewAllObjects ? this.allTeams : this.yourTeams;
          doType();
          resolve();
        });
      });
    });
  }
}
